import { IconMinus, IconPlus } from '@hubble/icons';
import { useCallback, useState, useEffect } from 'react';
import { Button } from '../Button/index.js';
import { StepperStyles, StepperInputStyles } from './styles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';

const Stepper = ({
  decrementDisabled = false,
  decrementLabel = 'Decrement',
  defaultValue = 0,
  disabled = false,
  incrementDisabled = false,
  incrementLabel = 'Increment',
  maxValue = Number.MAX_SAFE_INTEGER,
  minValue = Number.MIN_SAFE_INTEGER,
  name,
  onChange,
  onDecrement,
  onIncrement,
  precision,
  size = 'md',
  showValue = true,
  step = 1,
  value,
  variant = 'filled',
  width
}) => {
  const isControlled = value !== undefined;
  const clampValue = useCallback(value => {
    return Math.min(Math.max(value, minValue), maxValue);
  }, [minValue, maxValue]);
  const [internalValue, setInternalValue] = useState(clampValue(value !== null && value !== void 0 ? value : defaultValue));
  useEffect(() => {
    if (isControlled) {
      setInternalValue(clampValue(value));
    }
  }, [isControlled, value, clampValue]);
  const updateInternalValue = newValue => {
    const clampedValue = clampValue(newValue);
    setInternalValue(clampedValue);
    onChange === null || onChange === void 0 ? void 0 : onChange(clampedValue);
  };
  const handleIncrement = event => {
    if (!isControlled) {
      updateInternalValue(internalValue + step);
    }
    onIncrement === null || onIncrement === void 0 ? void 0 : onIncrement(event);
  };
  const handleDecrement = event => {
    if (!isControlled) {
      updateInternalValue(internalValue - step);
    }
    onDecrement === null || onDecrement === void 0 ? void 0 : onDecrement(event);
  };
  const handleInputChange = e => {
    if (!isControlled) {
      const newValue = parseInt(e.target.value, 10) || defaultValue;
      updateInternalValue(newValue);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(Number(e.target.value));
  };
  const displayValue = precision !== undefined ? internalValue.toFixed(precision) : internalValue;
  return jsxs(StepperStyles, {
    variant: variant,
    children: [jsx(Button.Tertiary, {
      "aria-label": decrementLabel,
      disabled: decrementDisabled || disabled,
      icon: jsx(IconMinus, {}),
      onClick: handleDecrement,
      size: size
    }), showValue && jsx(StepperInputStyles, {
      disabled: disabled,
      onChange: handleInputChange,
      value: displayValue,
      name: name,
      type: "text",
      readOnly: true,
      size: width || Math.max(String(displayValue).length, 1),
      inputSize: size
    }), jsx(Button.Tertiary, {
      "aria-label": incrementLabel,
      disabled: incrementDisabled || disabled,
      icon: jsx(IconPlus, {}),
      onClick: handleIncrement,
      size: size
    })]
  });
};
Stepper.displayName = 'Stepper';

export { Stepper };
